import axios from "axios";
import { authService } from "./authService";

const env = process.env.REACT_APP_ENVIRONMENT;
const apiBaseUrl =
  env === "LOCAL"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PRODUCTION;

// Axios instance
const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  timeout: 90000,
});

// Attach auth
apiClient.interceptors.request.use(async (config) => {
  const token = await authService.acquireToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ClientDetails.js helper
const mapClientViewToUI = (
  { id, created_at, client, business, person },
  {
    notes = [],
    tracking = [],
    dependants = [],
    ownership = [],
    credentials = null,
    quickbooks = null,
  } = {},
) => {
  const isPerson = client.client_type === "person";
  const base = {
    name: client.name,
    filing_status: client.filing_status,
    email: client.email,
    secondary_email: client.secondary_email ?? "",
    phone: client.phone,
    additional_phones: client.additional_phones ?? "",
    contact_method: client.contact_method ?? "",
    status: client.status ?? "",
    address: client.address ?? "",
    city: client.city ?? "",
    country: client.country ?? "",
    international: client.international ?? null,
    sales_rep_id: client.sales_rep_id ?? null,
    sales_rep_name: client.sales_rep_name ?? null,
    client_manager_id: client.client_manager_id ?? null,
    client_manager_name: client.client_manager_name ?? null,
    // documents_url: client.documents_url ?? null,
    language: client.language ?? "",
    ...(isPerson
      ? {
          spouse_name: person.spouse_name ?? null,
          filing_details: person.filing_details,
          state_filing: person.state_filing ?? null,
          ssn: person.ssn,
          spouse_ssn: person.spouse_ssn ?? null,
          date_birth: person.date_birth,
          spouse_date_birth: person.spouse_date_birth ?? null,
        }
      : {
          dba: business.dba ?? null,
          contact_name: business.contact_name,
          filing_state: business.filing_state ?? null,
          ein: business.ein,
          incorporation_date: business.incorporation_date ?? null,
          incorporation_state: business.incorporation_state ?? null,
          accounting_method: business.accounting_method ?? null,
          industry: business.industry ?? null,
        }),
  };

  return {
    id,
    created_at,
    type: isPerson ? "person" : "business",
    data: base,
    documents: { url: client.documents_url || "" },
    credentials,
    quickbooks,
    notes,
    tracking,
    dependants,
    ownership,
  };
};

class ClientService {
  // -------- Clients --------
  getClients = async (
    search = "",
    clientType = "all",
    limit = 50,
    skip = 0,
    filters = {},
  ) => {
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });
    if (search) params.append("search", search);
    if (clientType && clientType !== "all")
      params.append("client_type", clientType); // 'business'|'person'
    filters.salesRep?.forEach((name) => params.append("sales_rep", name));
    filters.clientManager?.forEach((name) =>
      params.append("client_manager", name),
    );
    const { data } = await apiClient.get(`/api/clients?${params}`);
    return data; // { items: ClientView[], total: number, limit, skip }
  };

  getClient = async (clientId) => {
    const [
      { data: cv },
      notes,
      tracking,
      dependants,
      ownership,
      credentials,
      quickbooks,
    ] = await Promise.all([
      apiClient.get(`/api/clients/${clientId}`),
      this.listNotes(clientId),
      this.listTracking(clientId),
      this.listDependants(clientId),
      this.listOwnership(clientId),
      this.getCredentials(clientId),
      this.getQuickBooks(clientId),
    ]);

    return mapClientViewToUI(cv, {
      notes,
      tracking,
      dependants,
      ownership,
      credentials,
      quickbooks,
    });
  };

  /**
   * Returns { exists: boolean, type?: 'person'|'business', client_id?: number, name?: string }
   */
  checkExists = async ({ ssn, ein }) => {
    const params = new URLSearchParams();
    if (ssn) params.append("ssn", ssn);
    if (ein) params.append("ein", ein);
    const { data } = await apiClient.get(`/api/clients/exists?${params}`);
    return data;
  };

  /**
   * payload shape must be:
   * {
   *   client: { client_type, name, email, phone, filing_status, ... },
   *   business?: { ein, contact_name, dba?, ... }  // required if client_type='business'
   *   person?: { filing_details, ssn, date_birth, ... } // required if client_type='person'
   * }
   */
  createClient = async (payload) => {
    try {
      const { data } = await apiClient.post("/api/clients", payload);
      return data;
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create client";
      throw new Error(msg);
    }
  };

  /**
   * updatePayload shape:
   * {
   *   client?: { ...fields... },
   *   business?: { ... } | person?: { ... }
   * }
   */
  updateClient = async (clientId, view) => {
    const isPerson = view.type === "person";
    const d = view.data || {};

    // 1) Core subtype
    const corePayload = {
      client: {
        client_type: isPerson ? "person" : "business",
        name: d.name,
        email: d.email,
        phone: d.phone,
        filing_status: d.filing_status,
        secondary_email: d.secondary_email || null,
        additional_phones: d.additional_phones || null,
        contact_method: d.contact_method || null,
        status: d.status || null,
        address: d.address || null,
        city: d.city || null,
        country: d.country || null,
        international: d.international || null,
        sales_rep_id: d.sales_rep_id || null,
        client_manager_id: d.client_manager_id || null,
        language: d.language || null,
        documents_url: view.documents?.url || null,
      },
      business: isPerson
        ? undefined
        : {
            dba: d.dba || null,
            filing_state: d.filing_state || null,
            ein: d.ein,
            incorporation_date: d.incorporation_date || null,
            incorporation_state: d.incorporation_state || null,
            accounting_method: d.accounting_method || null,
            industry: d.industry || null,
            contact_name: d.contact_name,
          },
      person: isPerson
        ? {
            spouse_name: d.spouse_name || null,
            filing_details: d.filing_details,
            state_filing: d.state_filing || null,
            ssn: d.ssn,
            spouse_ssn: d.spouse_ssn || null,
            date_birth: d.date_birth,
            spouse_date_birth: d.spouse_date_birth || null,
          }
        : undefined,
    };

    await apiClient.put(`/api/clients/${clientId}`, corePayload);

    // 2) Upsert singletons (quickbooks) reconcile credentials
    if (view.quickbooks && Object.keys(view.quickbooks || {}).length) {
      await this.upsertQuickBooks(clientId, view.quickbooks);
    }
    if (Array.isArray(view.credentials)) {
      const sanitize = (c) => ({
        entity_name: (c.entity_name ?? "").trim(),
        entity_link: (c.entity_link ?? "").trim(),
        username: (c.username ?? "").trim(),
        password: (c.password ?? "").trim(),
      });
      const next = view.credentials.filter((c) =>
        [c?.entity_name, c?.entity_link, c?.username, c?.password].some(
          (v) => (v ?? "").toString().trim().length > 0,
        ),
      );
      const current = await this.getCredentials(clientId);
      const currentById = new Map(current.map((c) => [String(c.id), c]));
      const nextById = new Map(
        next.filter((c) => c.id != null).map((c) => [String(c.id), c]),
      );

      for (const c of current) {
        if (!nextById.has(String(c.id))) {
          await this.deleteCredential(clientId, c.id);
        }
      }

      for (const c of next) {
        const payload = sanitize(c);
        const idStr = c.id != null ? String(c.id) : null;
        const existsOnServer = idStr && currentById.has(idStr);

        if (existsOnServer) {
          const before = currentById.get(idStr);
          const changed =
            before.entity_name !== payload.entity_name ||
            before.entity_link !== payload.entity_link ||
            before.username !== payload.username ||
            before.password !== payload.password;

          if (changed) {
            await this.updateCredential(clientId, Number(idStr), payload);
          }
        } else {
          await this.createCredential(clientId, payload);
        }
      }
    }

    // 3) Reconcile Dependants (add / update / delete)
    if (Array.isArray(view.dependants)) {
      const current = await this.listDependants(clientId);
      const currentById = new Map(current.map((d) => [String(d.id), d]));
      const nextById = new Map(
        view.dependants
          .filter((d) => d.id != null)
          .map((d) => [String(d.id), d]),
      );

      // Deletions: in current but not in next
      for (const d of current) {
        if (!nextById.has(String(d.id))) {
          await this.deleteDependant(clientId, d.id);
        }
      }

      // Additions Updates
      for (const d of view.dependants) {
        if (d.id == null) {
          // Add
          await this.addDependant(clientId, {
            name: d.name,
            date_birth: d.date_birth,
            ssn: d.ssn,
            relationship: d.relationship,
          });
        } else {
          // Update if changed
          const before = currentById.get(String(d.id));
          if (
            !before ||
            before.name !== d.name ||
            String(before.ssn) !== String(d.ssn) ||
            new Date(before.date_birth).toISOString() !==
              new Date(d.date_birth).toISOString() ||
            before.relationship !== d.relationship
          ) {
            await this.updateDependant(clientId, d.id, {
              name: d.name,
              date_birth: d.date_birth,
              ssn: d.ssn,
              relationship: d.relationship,
            });
          }
        }
      }
    }

    // 4) Reconcile Ownership (add / update / delete)
    if (Array.isArray(view.ownership)) {
      const current = await this.listOwnership(clientId);
      const currentById = new Map(current.map((o) => [String(o.id), o]));
      const nextById = new Map(
        view.ownership
          .filter((o) => o.id != null)
          .map((o) => [String(o.id), o]),
      );

      // Deletions
      for (const o of current) {
        if (!nextById.has(String(o.id))) {
          await this.deleteOwner(clientId, o.id);
        }
      }

      // Additions Updates
      for (const o of view.ownership) {
        if (o.id == null) {
          await this.addOwner(clientId, {
            name: o.name,
            ssn: o.ssn,
            date_birth: o.date_birth || null,
            percentage: Number(o.percentage),
            address: o.address || null,
          });
        } else {
          const before = currentById.get(String(o.id));
          if (
            !before ||
            before.name !== o.name ||
            String(before.ssn) !== String(o.ssn) ||
            new Date(before.date_birth).toISOString() !==
              new Date(o.date_birth).toISOString() ||
            Number(before.percentage) !== Number(o.percentage) ||
            before.address !== o.address
          ) {
            await this.updateOwner(clientId, o.id, {
              name: o.name,
              ssn: o.ssn,
              date_birth: o.date_birth || null,
              percentage: Number(o.percentage),
              address: o.address || null,
            });
          }
        }
      }
    }

    // 5) Return fresh hydrated UI object
    return this.getClient(clientId);
  };

  deleteClient = async (clientId) => {
    const { data } = await apiClient.delete(`/api/clients/${clientId}`);
    return data; // { message: string }
  };

  getSalesReps = async (q = "") => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    const { data } = await apiClient.get(`/api/sales-reps?${params}`);
    return data; // [{ id, name }]
  };

  createSalesRep = async (name) => {
    const { data } = await apiClient.post(`/api/sales-reps`, { name });
    return data; // { id, name }
  };

  deleteSalesRep = async (salesRepId) => {
    const { data } = await apiClient.delete(`/api/sales-reps/${salesRepId}`);
    return data;
  };

  getClientManagers = async (q = "") => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    const { data } = await apiClient.get(`/api/client-managers?${params}`);
    return data;
  };

  createClientManager = async (name) => {
    const { data } = await apiClient.post(`/api/client-managers`, { name });
    return data;
  };

  deleteClientManager = async (managerId) => {
    const { data } = await apiClient.delete(
      `/api/client-managers/${managerId}`,
    );
    return data;
  };

  exportClients = async ({ search, client_type } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (client_type && client_type !== "all")
      params.set("client_type", client_type);

    const res = await apiClient.get(
      `/api/clients/export?${params.toString()}`,
      {
        responseType: "blob",
        headers: {
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        validateStatus: (s) => s >= 200 && s < 300,
      },
    );

    const ct = (res.headers && res.headers["content-type"]) || "";
    const isExcel = ct.includes(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );

    if (!isExcel) {
      try {
        const text = await new Response(res.data).text();
        throw new Error(
          `Export failed. Content-Type: ${ct}. Body: ${text.slice(0, 300)}`,
        );
      } catch {
        throw new Error(`Export failed. Content-Type: ${ct}.`);
      }
    }

    return res.data;
  };

  // -------- Notes --------
  listNotes = async (clientId) => {
    const { data } = await apiClient.get(`/api/clients/${clientId}/notes`);
    return data; // NoteOut[]
  };

  /**
   * addNote:
   * - If you pass a string, it will be used as `content` with title "Note".
   * - To attach a file, first upload it elsewhere and pass the URL via `asset_url`.
   *   The API only accepts a string URL for asset_url (no multipart).
   *
   * examples:
   *   addNote(id, "Quick update")
   *   addNote(id, { title: "Kickoff", content: "Scope next steps", asset_url: "https://..." })
   */
  addNote = async (clientId, note) => {
    let body;
    if (typeof note === "string") {
      body = { title: "Note", content: note };
    } else if (note && typeof note === "object") {
      const { title, content, author, who_updated_last, asset_url } = note;
      body = {
        title: title ?? "Note",
        content,
        author,
        who_updated_last,
        asset_url,
      };
    } else {
      throw new Error("Invalid note payload");
    }
    const { data } = await apiClient.post(
      `/api/clients/${clientId}/notes`,
      body,
    );
    return data; // NoteOut
  };

  uploadNoteAttachment = async (clientId, { title, author, file }) => {
    const form = new FormData();
    form.append("title", title);
    form.append("author", author);
    form.append("file", file);

    const { data } = await apiClient.post(
      `/api/clients/${clientId}/notes/upload`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return data;
  };

  getNoteFileUrl = (clientId, path) => {
    const params = new URLSearchParams({ path });
    return `${apiBaseUrl}/api/clients/${clientId}/notes/file?${params}`;
  };

  getNoteFile = async (clientId, path) => {
    const res = await apiClient.get(`/api/clients/${clientId}/notes/file`, {
      params: { path },
      responseType: "blob",
      validateStatus: (s) => s >= 200 && s < 300,
    });
    const blob = res.data; // Blob
    const contentType =
      res.headers["content-type"] || "application/octet-stream";
    return { blob, contentType };
  };

  updateNote = async (
    clientId,
    noteId,
    { title, content, who_updated_last, asset_url = null },
  ) => {
    const { data } = await apiClient.put(
      `/api/clients/${clientId}/notes/${noteId}`,
      {
        title,
        content,
        who_updated_last,
        asset_url,
      },
    );
    return data;
  };

  deleteNote = async (clientId, noteId) => {
    const res = await apiClient.delete(
      `/api/clients/${clientId}/notes/${noteId}`,
    );
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  // -------- Tracking --------
  listTracking = async (clientId) => {
    const { data } = await apiClient.get(`/api/clients/${clientId}/tracking`);
    return data; // TrackingOut[]
  };

  /**
   * addTrackingEntry requires ISO datetimes:
   *   startedAt / endedAt must be strings like "2025-09-30T15:00:00Z"
   */
  addTrackingEntry = async (
    clientId,
    { activity_type, description, started_at, ended_at, created_by },
  ) => {
    const payload = {
      activity_type,
      description,
      started_at,
      ended_at,
      created_by,
    };
    const { data } = await apiClient.post(
      `/api/clients/${clientId}/tracking`,
      payload,
    );
    return data; // TrackingOut
  };

  deleteTrackingEntry = async (clientId, trackingId) => {
    const res = await apiClient.delete(
      `/api/clients/${clientId}/tracking/${trackingId}`,
    );
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  // -------- Dependants --------
  listDependants = async (clientId) => {
    const { data } = await apiClient.get(`/api/clients/${clientId}/dependants`);
    return data; // DependantOut[]
  };

  addDependant = async (clientId, dependant) => {
    // { name, date_birth (ISO), ssn, relationship }
    const { data } = await apiClient.post(
      `/api/clients/${clientId}/dependants`,
      dependant,
    );
    return data; // DependantOut
  };

  updateDependant = async (clientId, dependantId, dependant) => {
    const { data } = await apiClient.put(
      `/api/clients/${clientId}/dependants/${dependantId}`,
      dependant,
    );
    return data; // DependantOut
  };

  deleteDependant = async (clientId, dependantId) => {
    const res = await apiClient.delete(
      `/api/clients/${clientId}/dependants/${dependantId}`,
    );
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  // -------- Ownership --------
  listOwnership = async (clientId) => {
    const { data } = await apiClient.get(`/api/clients/${clientId}/ownership`);
    return data; // OwnershipOut[]
  };

  addOwner = async (clientId, owner) => {
    // { name, ssn, date_birth (ISO), percentage (number), address }
    const { data } = await apiClient.post(
      `/api/clients/${clientId}/ownership`,
      owner,
    );
    return data; // OwnershipOut
  };

  updateOwner = async (clientId, ownerId, owner) => {
    const { data } = await apiClient.put(
      `/api/clients/${clientId}/ownership/${ownerId}`,
      owner,
    );
    return data; // OwnershipOut
  };

  deleteOwner = async (clientId, ownerId) => {
    const res = await apiClient.delete(
      `/api/clients/${clientId}/ownership/${ownerId}`,
    );
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  // Credentials
  getCredentials = async (clientId) => {
    const { data } = await apiClient.get(
      `/api/clients/${clientId}/credentials`,
    );
    return data; // CredentialOut[]
  };

  createCredential = async (clientId, cred) => {
    const { data } = await apiClient.post(
      `/api/clients/${clientId}/credentials`,
      cred,
    );
    return data; // CredentialOut
  };

  updateCredential = async (clientId, credentialId, cred) => {
    const { data } = await apiClient.put(
      `/api/clients/${clientId}/credentials/${credentialId}`,
      cred,
    );
    return data; // CredentialOut
  };

  deleteCredential = async (clientId, credentialId) => {
    const res = await apiClient.delete(
      `/api/clients/${clientId}/credentials/${credentialId}`,
    );
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  // -------- QuickBooks (unique per client) --------
  getQuickBooks = async (clientId) => {
    const { data } = await apiClient.get(`/api/clients/${clientId}/quickbooks`);
    return data; // QuickBooksOut | null
  };

  upsertQuickBooks = async (clientId, qb) => {
    // { type, username, password }
    const { data } = await apiClient.post(
      `/api/clients/${clientId}/quickbooks`,
      qb,
    );
    return data; // QuickBooksOut
  };

  deleteQuickBooks = async (clientId) => {
    const { data } = await apiClient.delete(
      `/api/clients/${clientId}/quickbooks`,
    );
    return data;
  };
}

export const clientService = new ClientService();
